

































































import {
  ComponentInstance,
  computed,
  defineComponent,
  inject,
  Ref,
  ref,
  useRoute,
} from '@nuxtjs/composition-api';
import { SearchPreFiltersControl } from '@shared/components/SearchPreFilters';
import useFeatureFlag from '@shared/composables/useFeatureFlag';
import useResponsive from '@shared/composables/useResponsive';
import { CSSClass } from '@shared/types/vue';
import { BaseOverlay, BaseSearchBar, UCIcon } from '@shared/ui';
import { ISearchProductsResponse } from '@unified-commerce/gpc-vue-storefront-search';
import debounce from 'lodash/debounce';
import clickOutside from 'vue-click-outside';

import { useSearchPreFilters } from '~/composables/useSearchPreFilters';
import { SEARCH_QUERY_PARAM } from '~/constants';

import SearchBarResults from '../SearchBarResults/SearchBarResults.vue';

export default defineComponent({
  name: 'SearchBar',
  directives: {
    clickOutside,
  },
  components: {
    BaseSearchBar,
    SearchBarResults,
    BaseOverlay,
    SearchPreFiltersControl,
    UCIcon,
  },
  props: {
    placeholder: {
      type: String,
      default: 'What are you shopping for?',
    },
  },
  setup() {
    const route = useRoute();
    const { isMobile } = useResponsive();
    const { isFeatureEnabled } = useFeatureFlag();
    // Non-prod only: in prod this is false, so the template renders the original search bar.
    const preFiltersEnabled = isFeatureEnabled('searchPreFilters');
    const searchResult = inject<Ref<ISearchProductsResponse>>('searchResult');
    const searchResultLoading = inject('searchResultLoading');

    const computedSearchResult = computed(() => searchResult?.value ?? []);
    const searchValue = ref(route.value.query[SEARCH_QUERY_PARAM] ?? '');

    // Per-brand singleton; the SearchPreFiltersControl owns the dialog + search/clear glue.
    const searchPreFilters = useSearchPreFilters();

    return {
      isMobile,
      preFiltersEnabled,
      searchValue,
      computedSearchResult,
      searchResultLoading,
      searchPreFilters,
    };
  },
  data() {
    const isSearchResultsOpen = false;
    const isMobileSearchResultsOpen = false;

    return {
      isSearchResultsOpen,
      isMobileSearchResultsOpen,
    };
  },
  computed: {
    searchIcon(): object {
      let icon = 'search';
      if (this.searchValue || this.isMobileSearchResultsOpen) {
        icon = 'cross';
      }
      return {
        icon,
        size: 'var(--font-size--base)',
        color: 'var(--c-primary)',
      };
    },
    // On mobile the suggestions cover the screen and there is no overlay to tap, so the icon
    // is the only way out. Desktop keeps the magnifying glass — BaseOverlay dismisses there.
    showCloseIcon(): boolean {
      return Boolean(this.searchValue) || (this.isMobile && this.isSearchResultsOpen);
    },
    searchBarClasses(): CSSClass[] {
      const baseClass = 'search-bar';
      const focusedClass = `${baseClass}--focused`;

      return [
        baseClass,
        {
          [focusedClass]: this.isMobileSearchResultsOpen,
        },
      ];
    },
    searchBarInputClasses(): CSSClass[] {
      const baseClass = 'search-bar__input';
      const focusedClass = `${baseClass}--focused`;

      return [
        baseClass,
        {
          [focusedClass]: this.isMobileSearchResultsOpen,
        },
      ];
    },
    searchBarResultsClasses(): CSSClass[] {
      const baseClass = 'search-bar__results';
      const focusedClass = `${baseClass}--focused`;

      return [
        baseClass,
        {
          [focusedClass]: this.isMobileSearchResultsOpen,
        },
      ];
    },
    searchBarInputClassesOriginal(): CSSClass[] {
      return [...this.searchBarInputClasses, 'search-bar__input--original'];
    },
  },
  watch: {
    '$route.fullPath'(val): void {
      if (!val.includes('/search')) {
        this.searchValue = '';
      } else {
        this.searchValue = !Array.isArray(this.$route.query.q) ? this.$route.query.q : '';
      }
      this.closeSearchResults();
    },
  },
  methods: {
    searchInput: debounce(function (this: ComponentInstance, input: string) {
      if (input && input !== this.$route.query.q) {
        this.$options?.methods?.openSearchResults?.call(this);
      }
      this.$options?.methods?.emitSearch?.call(this);
    }, 500),
    emitSearch(): void {
      this.$emit('search', this.searchValue);
    },
    emitSearchEnter(): void {
      this.$emit('search-enter', this.searchValue);
      this.closeSearchResults();
    },
    closeSearchResults(): void {
      this.isSearchResultsOpen = false;
      this.closeMobileSearchResults();
    },
    openSearchResults(): void {
      this.isSearchResultsOpen = true;
      this.openMobileSearchResults();
    },
    onEnter(): void {
      this.emitSearchEnter();
      this.closeSearchResults();
    },
    closeIconClick(): void {
      this.searchValue = '';
      this.closeSearchResults();
    },
    onFocus(): void {
      this.emitSearch();
      this.openSearchResults();
    },
    onCancel(): void {
      this.closeSearchResults();
    },
    openMobileSearchResults(): void {
      if (!this.isMobileSearchResultsOpen) {
        const html = document.querySelector('html');
        html?.classList.add('modal-open');
        this.isMobileSearchResultsOpen = true;
      }
    },
    closeMobileSearchResults(): void {
      if (this.isMobileSearchResultsOpen) {
        const html = document.querySelector('html');
        html?.classList.remove('modal-open');
        this.isMobileSearchResultsOpen = false;
      }
    },
    onActiveQueryClicked(): void {
      this.closeSearchResults();
    },
  },
});
